// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { CaretDown } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { type ReactNode } from "react";

import { ASK_V2_RAIL_TOGGLE_CLASS } from "../rail/AskV2RailSection";

/**
 * One section of the Ask v2 findings list: its heading, the toggle with the
 * count, and the list it opens. Presentational -- which findings sit in it and
 * whether it is open belong to `AskV2ReviewCommentsList`.
 */
export function AskV2ReviewCommentSection({
  sectionKey,
  label,
  count,
  open,
  listId,
  onToggle,
  children,
}: {
  sectionKey: string;
  label: string;
  count: number;
  open: boolean;
  listId: string;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <li className="mb-3" data-testid={`ask-v2-review-section-${sectionKey}`}>
      <h3
        className="m-0"
        data-testid={`ask-v2-review-section-${sectionKey}-heading`}
      >
        <button
          type="button"
          // why: same chrome as a chat rail toggle — size-1 medium, caret on the right — not the 15px semibold heading.
          className={`${ASK_V2_RAIL_TOGGLE_CLASS} mb-2 rounded-md hover:bg-puntt-neutral-gray-2`}
          data-testid={`ask-v2-review-section-${sectionKey}-toggle`}
          aria-expanded={open}
          // why: a closed section's list is not rendered, and aria-controls
          // must name an element that exists.
          aria-controls={open ? listId : undefined}
          onClick={onToggle}
        >
          <Text
            as="span"
            size="1"
            weight="medium"
            className="shrink-0 text-puntt-neutral-grayA-11"
          >
            {label}
          </Text>
          <span
            className="shrink-0 font-medium text-puntt-neutral-gray-10"
            data-testid={`ask-v2-review-section-${sectionKey}-count`}
          >
            {count}
          </span>
          <CaretDown
            size={14}
            aria-hidden="true"
            className={`ml-auto shrink-0 text-puntt-neutral-gray-11 transition-transform ${open ? "" : "-rotate-90"}`}
          />
        </button>
      </h3>
      {open ? (
        <ul
          id={listId}
          data-testid={`ask-v2-review-section-${sectionKey}-list`}
        >
          {children}
        </ul>
      ) : null}
    </li>
  );
}
